import { Action } from 'typescript-fsa';
import * as dotProp from 'dot-prop-immutable';

import { createReducer } from '@topconagriculture/frontend-utils';

import { Ingredient, IngredientDailyUsage } from './ingredients.types';
import {
  addIngredientInventoryStock,
  addIngredientRequest,
  deleteIngredientRequest,
  editIngredientRequest,
  getAllIngredientsRequest,
  getIngredientDailyUsage,
  getIngredientRequest,
  getIngredientsListRequest,
} from './ingredients.actions';

export type IngredientState = {
  ingredientList: Ingredient[];
  dailyUsage: IngredientDailyUsage;
};

const initialState: IngredientState = {
  ingredientList: [],
  dailyUsage: {
    total: 0,
    average: 0,
    usageDays: [],
  },
};

const ingredientsReducer = createReducer(initialState, {
  [getIngredientsListRequest.done.type]: (state: IngredientState, action: Action<any>) => {
    const ingredients: Ingredient[] = action.payload.result;
    const ingredientsIds = ingredients.map((ing) => ing.id);
    const oldIngredients = state.ingredientList.filter((ing) => !ingredientsIds.includes(ing.id));
    return {
      ...state,
      ingredientList: [...ingredients, ...oldIngredients].sort((elem1: Ingredient, elem2: Ingredient) => {
        return elem1.description.toLowerCase().localeCompare(elem2.description.toLowerCase());
      }),
    };
  },
  [getAllIngredientsRequest.done.type]: (state: IngredientState, action: Action<any>) => {
    const ingredients: Ingredient[] = action.payload.result;
    const newIngredients = ingredients.map((x) => ({
      ...state.ingredientList.find((ing) => ing.id === x.id),
      ...x,
    }));
    return {
      ...state,
      ingredientList: newIngredients.sort((elem1: Ingredient, elem2: Ingredient) => {
        return elem1.description.toLowerCase().localeCompare(elem2.description.toLowerCase());
      }),
    };
  },
  [addIngredientRequest.done.type]: (state: IngredientState, action: Action<any>) => ({
    ...state,
    ingredientList: [...state.ingredientList, ...[action.payload.result]],
  }),
  [editIngredientRequest.done.type]: (state: IngredientState, action: Action<any>) => {
    const editedIngredient: Ingredient = action.payload.result;
    const indexFound = state.ingredientList.findIndex(
      (ingredient: Ingredient) => ingredient.id === editedIngredient.id,
    );
    return dotProp.set(state, `ingredientList.${indexFound}`, editedIngredient);
  },
  [deleteIngredientRequest.done.type]: (state: IngredientState, action: Action<any>) => {
    const deletedIngredientId: string = action.payload.result;
    const indexFound = state.ingredientList.findIndex(
      (ingredient: Ingredient) => ingredient.id === deletedIngredientId,
    );
    return dotProp.delete(state, `ingredientList.${indexFound}`);
  },
  [getIngredientRequest.done.type]: (state: IngredientState, action: Action<{ result: Ingredient }>) => {
    const ingredient = action.payload.result;
    return {
      ...state,
      ingredientList: state.ingredientList.map((x) => (x.id === ingredient.id ? ingredient : x)),
    };
  },
  [getIngredientDailyUsage.done.type]: (
    state: IngredientState,
    action: Action<{ result: IngredientDailyUsage[] }>,
  ) => ({
    ...state,
    dailyUsage: action.payload.result,
  }),
  [addIngredientInventoryStock.done.type]: (
    state: IngredientState,
    action: ReturnType<typeof addIngredientInventoryStock.done>,
  ) => {
    const { ingredientId } = action.payload.params;
    const ingredientList = state.ingredientList.map((ingredient) =>
      ingredient.id === ingredientId
        ? {
            ...ingredient,
            stock: action.payload.result.stock,
          }
        : ingredient,
    );
    return {
      ...state,
      ingredientList,
    };
  },
});

export default ingredientsReducer;
